import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { Space, Table, Button } from 'antd';
import { isArray } from 'lodash';
import { useReactToPrint } from 'react-to-print';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearFiltersData,
  setFiltersData,
  setPaginationConfig,
  clearPaginationConfig,
} from '../../../appRedux/actions/General';
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { buttons } from '../../../constants/Buttons.const';
import { useLocation } from 'react-router-dom';
import './styles.css';

const hasFilterValue = ([key, value]) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;

  if (key === 'search') {
    return Boolean(
      value?.filter &&
        value?.value !== undefined &&
        value?.value !== null &&
        value?.value !== ''
    );
  }

  return true;
};

export const ListTable = ({
  columns,
  size = 'middle',
  standOut = false,
  rowClass = null,
  component = null,
  getDataFunction,
  params = {},
  canExport = true,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { filtersData, previousPath, paginationConfig } = useSelector(
    (state) => state?.generalStates
  );
  const { dataDeleted, dataUpdated } = useSelector((state) => state?.common);
  const { maxMin, stockAdjustment } = useSelector((state) => state?.stock);

  const [dataSource, setDataSource] = useState([]);
  const [totalRows, setTotalRows] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [name, setName] = useState();
  const [shouldCallData, setShouldCallData] = useState(false);

  const totalDataLabel = useMemo(() => (totalRows && buttons.allData.label),[totalRows]);
  const pageSizeOptions = useMemo(() => {
    const basePageSizeOptions = [5, 10, 15, 30, 45, 60];
    return canExport ? [...basePageSizeOptions, totalDataLabel] : basePageSizeOptions;
  }, [canExport, totalDataLabel]);

  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => {
      const component = componentRef.current;
      if (component) component.style.display = 'block';
      return component;
    },
  });

  const revertStyle = useCallback(() => {
    const component = componentRef.current;
    if (component) component.style.display = 'none';
  }, []);

  const leaf = (obj, column) => {
    let value = '';
    const { path, formatter } = column;
    if (path === 'default') {
      return formatter(obj);
    }
    if (isArray(path)) {
      let values = [];
      for (const p of path) {
        const leafValue =
          p.split('.').reduce((value, el) => value?.[el], obj) ?? '';
        values.push(`${leafValue}`);
        value = formatter ? formatter(values) : values.join(' ');
      }
    } else {
      const leafValue =
        path.split('.').reduce((value, el) => value?.[el], obj) ?? '';
      value = formatter ? formatter(leafValue) : leafValue;
    }
    return value;
  };

  const getExportData = useCallback(
    (baseData) => {
      const dataToExport = [];
      const result = baseData?.map((element, index) => {
        const result = {};
        for (const column of columns) {
          if (column.title !== 'Opciones') {
            result[column.title] =
              column.path === undefined
                ? element[column.dataIndex]
                : leaf(element, column);
          }
        }
        dataToExport.push(result);
        return {
          ...element,
          key: index,
        };
      });
      return { result, dataToExport };
    },
    [columns]
  );

  const getData = useCallback(
    (page = 1, take = 15, order, field) => {
      const tableParams = Object.fromEntries(
        Object.entries({ ...params, ...filtersData }).filter(hasFilterValue)
      );

      setLoading(true);
      dispatch(
        getDataFunction({
          page,
          take,
          limit: 1,
          order,
          field,
          active: 1,
          withDeleted: 'false',
          ...tableParams,
        })
      ).then((res) => {
        if (res) {
          setDataSource(res?.data?.items ?? []);
          setTotalRows(res?.data?.meta?.itemCount ?? 0);
          setLoading(false);
        }
      });
    },
    [dispatch, getDataFunction, params, filtersData]
  );

  useEffect(() => {
    if (dataSource.length > 0) {
      const { dataToExport } = getExportData(dataSource);
      setExportData(dataToExport);
    }
  }, [dataSource, getExportData]);

  useEffect(() => {
    const currentPath = location.pathname;
    const [currentLocation] = currentPath.split('/').slice(2);
    setName(currentLocation);
    if (previousPath && previousPath.includes(currentLocation)) {
      setShouldCallData(true);
      return;
    }
    // Se limpia los filtros y la paginación si el path actual es diferente al path anterior
    dispatch(clearPaginationConfig());
    dispatch(clearFiltersData());

    if (params.status !== null) {
      dispatch(setFiltersData({ status: 1 }));
    }

    setShouldCallData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldCallData) return;

    if (paginationConfig?.page && paginationConfig?.take) {
      getData(paginationConfig.page, paginationConfig.take);
    } else {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCallData, filtersData, paginationConfig?.page, paginationConfig?.take, dataDeleted, maxMin, stockAdjustment, dataUpdated]);

  return (
    <>
      <Space
        style={{
          marginTop: 20,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {canExport && (
          <>
            <Space>
              <Button
                onClick={() => {
                  handlePrint();
                  revertStyle();
                }}
                className="btn-form"
                disabled={loading}
                icon={<FilePdfOutlined />}
                style={{ backgroundColor: '#ca0b0b', color: '#fff' }}
              >
                {buttons.exportPDF.label}
              </Button>
              <CSVLink
                filename={`${name || 'export'}.csv`}
                data={exportData}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  className="btn-form"
                  icon={<FileExcelOutlined />}
                  disabled={loading || exportData.length === 0}
                  style={{ backgroundColor: '#009929', color: '#fff' }}
                >
                  {buttons.exportCSV.label}
                </Button>
              </CSVLink>
            </Space>
            <Space style={{ marginRight: '10px' }}>
              Total de registros: {totalRows}
            </Space>
          </>
        )}
        {component}
      </Space>

      <div
        ref={componentRef}
        style={{
          display: 'none',
          padding: 50,
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          background: '#fff',
          zIndex: 9999,
        }}
      >
        <Table
          size="middle"
          className="gx-table-responsive export-table"
          columns={columns.filter((column) => column.title !== 'Opciones')}
          dataSource={dataSource}
          pagination={false}
          bordered
        />
      </div>

      <Table
        loading={loading}
        size={size}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          defaultPageSize: 15,
          pageSize: paginationConfig?.take ?? 15,
          current: paginationConfig?.page ?? 1,
          defaultCurrent: 1,
          pageSizeOptions,
          total: totalRows,
          showSizeChanger: true,
          locale: { items_per_page: '' },
          onChange: (page, take) => {
            const itemsPerPage = isNaN(take) ? totalRows : take;
            getData(page, itemsPerPage);
            dispatch(setPaginationConfig({ page, take: itemsPerPage }));
          },
        }}
         onChange={(page, _filters, sort, extra) => {
          getData(page.current, page.pageSize, sort.order, sort.field);
          setExportData(getExportData(extra.currentDataSource).dataToExport);
        }}
        bordered
        rowClassName={standOut && rowClass ? rowClass : undefined}
      />
    </>
  );
};
